import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LocuLogo from '../components/LocuLogo';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { verifyOTP, loadMSG91Widget } from '../utils/msg91Widget';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, setToken, setUser } = useAuth();

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef([]);

  // Get phone from location state
  const phone = location.state?.phone || sessionStorage.getItem('signup_phone') || '';
  const isMerchant = location.state?.isMerchant || false;

  // Redirect authenticated users away from OTP page
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Initialize MSG91 widget
  useEffect(() => {
    const initWidget = async () => {
      try {
        await loadMSG91Widget();
      } catch (err) {
        console.error('Widget init error:', err);
        setError('Failed to initialize OTP service. Please refresh.');
      }
    };
    initWidget();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Handle input change
  const handleChange = (index, value) => {
    if (!/^[0-9]*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (index === 5 && value) {
      const fullOtp = [...newOtp.slice(0, 5), value].join('');
      if (fullOtp.length === 6) {
        setTimeout(() => handleVerify(fullOtp), 100);
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).replace(/[^0-9]/g, '');
    if (pastedData.length === 6) {
      setOtp(pastedData.split(''));
      setTimeout(() => handleVerify(pastedData), 100);
    }
  };

  // Verify OTP
  const handleVerify = async (fullOtp) => {
    const otpToVerify = fullOtp || otp.join('');
    
    if (otpToVerify.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify OTP using MSG91 widget
      const verifyResult = await verifyOTP(otpToVerify);
      
      if (verifyResult.success) {
        // Verify widget token with backend
        const token = verifyResult.token;
        
        const response = await axios.post(`${API_URL}/auth/verify-widget-token`, {
          token,
          phone
        });

        if (response.data.newUser) {
          // New user - navigate to registration
          navigate('/register', {
            state: {
              phone,
              isMerchant
            }
          });
        } else {
          // Existing user - login
          setToken(response.data.token);
          setUser(response.data.user);

          // Clear merchant flags if logged in as regular user
          if (response.data.user.role !== 'merchant') {
            localStorage.removeItem('merchantIntent');
            localStorage.removeItem('locuUserType');
            sessionStorage.removeItem('isMerchant');
          } else {
            // Set merchant view for restaurant users
            localStorage.setItem('locuUserType', 'merchants');
          }

          // Navigate based on role
          if (response.data.user.role === 'merchant') {
            navigate('/merchant/dashboard');
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        setError('Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to verify OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (!canResend || loading) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Check if widget method is available
      if (!window.sendOtp) {
        // Try to load widget first
        await loadMSG91Widget();
      }
      
      if (!window.sendOtp) {
        setError('OTP service not available. Please refresh the page.');
        setLoading(false);
        return;
      }
      
      // Trigger sendOTP with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timed out')), 15000)
      );
      
      const sendPromise = new Promise((resolve, reject) => {
        window.sendOtp(
          phone.replace('+', ''),
          (data) => {
            console.log('Resend OTP success:', data);
            resolve(data);
          },
          (err) => {
            console.error('Resend OTP error:', err);
            reject(err);
          }
        );
      });
      
      await Promise.race([sendPromise, timeoutPromise]);
      
      // Reset state on success
      setCountdown(30);
      setCanResend(false);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      
    } catch (err) {
      console.error('Resend error:', err);
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format phone for display
  const formatPhone = (phoneNum) => {
    if (!phoneNum) return '';
    const cleaned = phoneNum.replace(/\D/g, '');
    if (cleaned.length === 12 && cleaned.startsWith('91')) {
      return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
    }
    return phoneNum;
  };

  if (!phone) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Session expired. Please start again.</p>
          <Link to="/login" className="text-primary-600 hover:underline">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-4 py-12">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <LocuLogo showText={true} textSize="text-2xl" textColor="text-gray-900" />
          </Link>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/login', { state: { isMerchant } })}
          className="mb-8 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </button>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Enter OTP
          </h1>
          <p className="text-gray-600">
            We've sent a 6-digit code to<br />
            <span className="font-semibold text-gray-900">{formatPhone(phone)}</span>
          </p>
        </div>

        {/* OTP Input */}
        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">
              {error}
            </div>
          )}

          <div className="flex justify-center gap-2 sm:gap-3">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={loading}
                className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-0 outline-none transition-colors disabled:bg-gray-100"
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            onClick={() => handleVerify()}
            disabled={loading || otp.join('').length !== 6}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify OTP'
            )}
          </button>

          {/* Resend Section */}
          <div className="text-center">
            {canResend ? (
              <button
                onClick={handleResend}
                disabled={loading}
                className="flex items-center justify-center gap-2 mx-auto text-primary-600 hover:text-primary-700 font-medium"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Resend OTP
              </button>
            ) : (
              <p className="text-gray-500">
                Resend OTP in <span className="font-semibold">{countdown}s</span>
              </p>
            )}
          </div>

          {/* Change Number */}
          <p className="text-center text-sm text-gray-500">
            Wrong number?{' '}
            <button
              onClick={() => navigate('/login', { state: { isMerchant } })}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Change
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
